import React from 'react';
import { InlineGrid, Card, Text, Box, SkeletonBodyText, InlineStack, Badge } from '@shopify/polaris';
import { calculateOrderSuccessRate } from '../utils/orderQuickStats';

/**
 * QuickStatsCards Component
 * Displays quick statistics for orders dashboard
 * Mirrors the canonical order lifecycle plus unresolved issues.
 */
const QuickStatsCards = ({ stats, loading, onStatClick, selectedStatKey }) => {
  // Default stats structure
  const defaultStats = {
    total: 0,
    pending_confirmation: 0,
    pending_generation: 0,
    pending_printing: 0,
    pending_delivery: 0,
    picked_up: 0,
    delivered: 0,
    returned: 0,
    cancelled: 0,
    flagged: 0
  };

  const currentStats = stats || defaultStats;

  // Format number with commas
  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };

  const primaryStats = [
    {
      key: 'total',
      label: 'Orders Placed',
      value: currentStats.total,
      tone: 'info',
      filterValue: [],
      description: ''
    },
    {
      key: 'delivered',
      label: 'Delivered',
      value: currentStats.delivered,
      tone: 'success',
      filterValue: ['delivered'],
      description: ''
    },
    {
      key: 'success_rate',
      label: 'Success Rate',
      value: `${calculateOrderSuccessRate(currentStats.delivered, currentStats.total)}%`,
      tone: 'success',
      filterValue: ['delivered'],
      description: ''
    }
  ];
  const issuesStat = {
    key: 'flagged',
    label: 'Issues',
    value: currentStats.flagged,
    tone: 'critical',
    filterValue: 'has_flags',
    description: 'Needs attention'
  };

  const lifecycleStats = [
    {
      key: 'pending_confirmation',
      label: 'Pending Confirmation',
      value: currentStats.pending_confirmation ?? currentStats.pending ?? 0,
      tone: 'attention',
      filterValue: ['pending_confirmation'],
      description: 'New Order'
    },
    {
      key: 'pending_generation',
      label: 'Pending Generation',
      value: currentStats.pending_generation ?? currentStats.in_progress ?? 0,
      tone: 'info',
      filterValue: ['pending_generation'],
      description: 'Confirmed'
    },
    {
      key: 'pending_printing',
      label: 'Pending Printing',
      value: currentStats.pending_printing ?? 0,
      tone: 'warning',
      filterValue: ['pending_printing'],
      description: 'Generated'
    },
    {
      key: 'returned',
      label: 'Returned',
      value: currentStats.returned,
      tone: 'warning',
      filterValue: ['returned'],
      description: 'Returned orders'
    },
    {
      key: 'pending_delivery',
      label: 'Pending Delivery',
      value: currentStats.pending_delivery ?? currentStats.shipping ?? 0,
      tone: 'warning',
      filterValue: ['pending_delivery'],
      description: 'On Hub'
    },
    {
      key: 'picked_up',
      label: 'Picked Up',
      value: currentStats.picked_up ?? 0,
      tone: 'info',
      filterValue: ['picked_up'],
      description: 'Out for Delivery'
    },
    {
      key: 'cancelled',
      label: 'Cancelled',
      value: currentStats.cancelled,
      tone: 'critical',
      filterValue: ['cancelled'],
      description: ''
    }
  ];

  // Handle card click
  const handleCardClick = (config) => {
    if (onStatClick && config.filterValue !== undefined) {
      onStatClick(config.key, config.filterValue);
    }
  };

  const toneColors = {
    attention: '#8a6116',
    critical: '#d72c0d',
    info: '#2c6ecb',
    success: '#008060',
    warning: '#b98900'
  };

  const renderCard = (config, className = '') => (
    <div
      key={config.key}
      className={className}
      onClick={() => handleCardClick(config)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleCardClick(config);
        }
      }}
      role="button"
      tabIndex={0}
      aria-pressed={selectedStatKey === config.key}
      style={{
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        borderRadius: '12px',
        border: `2px solid ${
          selectedStatKey === config.key
            ? toneColors[config.tone] || '#2c6ecb'
            : 'transparent'
        }`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <Card padding="400">
        {loading ? (
          <SkeletonBodyText lines={2} />
        ) : (
          <Box>
            <InlineStack align="start">
              <Badge tone={config.tone} size="small">
                {config.label}
              </Badge>
            </InlineStack>

            <Box paddingBlockStart="200">
              <Text variant="heading2xl" as="h3">
                {typeof config.value === 'string'
                  ? config.value
                  : formatNumber(config.value)}
              </Text>
            </Box>

            {config.description && (
              <Box paddingBlockStart="100">
                <InlineStack align="end">
                  <Text variant="bodySm" as="p" tone="subdued">
                    {config.description}
                  </Text>
                </InlineStack>
              </Box>
            )}
          </Box>
        )}
      </Card>
    </div>
  );

  const renderCards = (configs, columns) => (
    <InlineGrid columns={columns} gap="400">
      {configs.map((config) => renderCard(config))}
    </InlineGrid>
  );

  return (
    <Box paddingBlockEnd="400">
      <Box paddingBlockEnd="400">
        <div className="orders-primary-stats-grid">
          {primaryStats.map((config) => renderCard(config))}
          {renderCard(issuesStat, 'orders-issues-stat-card')}
        </div>
      </Box>
      {renderCards(lifecycleStats, { xs: 1, sm: 2, md: 4, lg: 7 })}
    </Box>
  );
};

export default QuickStatsCards;
